import { getUniqueCurrentISOTimestamp } from '@app/core/get-unique-current-iso-timestamp'
import { Timespan } from '@app/modules/time/core/timespan.enum'

import type { SendMessagesPayload } from '../../models/api.model'
import type { MessageForm } from '../../models/message-form.model'
import type { Message } from '../../models/message.model'
import type { MessagePhotoAlbumSection } from '../../modules/message-photo/models/api.model'
import type { MessagePhotoAlbumPhoto } from '../../modules/message-photo/models/message-photo-album-photo.model'
import type { UnsentVoiceMessage } from '../../modules/voice-message/models/voice-message.model'

const photoToFakeMessage = (
  photo: MessagePhotoAlbumPhoto,
  section: MessagePhotoAlbumSection,
  photos_expiration_hours: number,
): Message => {
  const timestamp = getUniqueCurrentISOTimestamp()

  return {
    type: 'photo',
    boosted: false,
    fe_message_id: timestamp,
    is_me: true,
    timestamp: timestamp,
    ...(section === 'pending'
      ? {
          status: 'pending',
          photo: {
            thumb_url: photo.thumb_url,
            fe_hash: photo.fe_hash,
          },
        }
      : {
          status: 'viewable',
          photo: {
            expires_at: new Date(new Date(timestamp).getTime() + photos_expiration_hours * Timespan.Hour).toISOString(),
            thumb_url: photo.thumb_url,
            url: photo.thumb_url,
            fe_hash: photo.fe_hash,
          },
        }),
  }
}

const voiceToFakeMessage = (
  voiceUrl: string,
  duration: number,
  boosted: boolean,
  voice_messages_expiration_hours: number,
): Message => {
  const timestamp = getUniqueCurrentISOTimestamp()

  return {
    type: 'voice',
    boosted,
    fe_message_id: timestamp,
    is_me: true,
    timestamp: timestamp,
    status: 'viewable',
    voice_message: {
      expires_at: new Date(
        new Date(timestamp).getTime() + voice_messages_expiration_hours * Timespan.Hour,
      ).toISOString(),
      url: voiceUrl,
      duration,
    },
  }
}

export const formValueToSendMessagesPayload = (messageForm: MessageForm | undefined) => {
  const fakeMessages: Message[] = []
  const payload: SendMessagesPayload = {
    request_or_share_access: !!messageForm?.request_or_share_access,
    messages: [],
  }

  if (messageForm?.body) {
    const timestamp = getUniqueCurrentISOTimestamp()

    fakeMessages.push({
      type: 'text',
      body: messageForm.body,
      boosted: false,
      fe_message_id: timestamp,
      is_me: true,
      timestamp,
    })

    payload.messages.push({
      type: 'text',
      body: messageForm.body,
      fe_message_id: timestamp,
    })
  }

  if (messageForm?.boosted) {
    payload.messages[payload.messages.length - 1]!.boosted = true
    fakeMessages[fakeMessages.length - 1]!.boosted = true
  }

  return { payload, fakeMessages }
}

export const photosToSendMessagesPayload = (
  section: MessagePhotoAlbumSection,
  photos: MessagePhotoAlbumPhoto[],
  messageForm: MessageForm | undefined,
  photos_expiration_hours: number,
) => {
  const fakeMessages: Message[] = []
  const payload: SendMessagesPayload = {
    request_or_share_access: !!messageForm?.request_or_share_access,
    messages: [],
  }

  photos.forEach(photo => {
    const fakeMessage = photoToFakeMessage(photo, section, photos_expiration_hours)
    fakeMessages.push(fakeMessage)
    payload.messages.push({ type: 'photo', fe_message_id: fakeMessage.timestamp, album_photo_id: photo.id })
  })

  return { payload, fakeMessages }
}

export const sliceAffordableMessages = <T extends { type: string }>(maxCount: number, items: T[]): T[] => {
  let photosCount = 0
  const result: T[] = []
  for (const item of items) {
    if (item.type === 'photo') {
      if (photosCount >= maxCount) {
        break
      }
      photosCount++
    }
    result.push(item)
  }
  return result
}

export const voiceToSendMessagesPayload = (
  voice: UnsentVoiceMessage,
  messageForm: MessageForm | undefined,
  boosted: boolean,
  voice_messages_expiration_hours: number,
) => {
  const payload: SendMessagesPayload = {
    request_or_share_access: !!messageForm?.request_or_share_access,
    messages: [],
  }

  const fakeMessage = voiceToFakeMessage(voice.url, voice.duration, boosted, voice_messages_expiration_hours)
  payload.messages.push({
    type: 'voice',
    fe_message_id: fakeMessage.timestamp,
    voice_message_id: voice.id,
    ...(boosted && { boosted: true }),
  })

  return { payload, fakeMessage }
}
